// Shared helpers for video embeds, used by ElementVideo / ElementQuiz (builder)
// and the live-site video / quiz element scripts.
class VideoService {
  static detectProvider(url) {
    if (!url) return null;
    const value = url.toLowerCase();
    if (value.indexOf("youtube") > -1 || value.indexOf("youtu.be") > -1) {
      return "youtube";
    }
    if (value.indexOf("vimeo") > -1) return "vimeo";
    if (value.indexOf("wistia") > -1) return "wistia";
    return "unknown";
  }

  static youtubeVideoId(url) {
    if (!url) return null;
    if (url.indexOf("watch?v=") > -1) {
      return url.split("watch?v=")[1].split("&")[0].split("#")[0];
    }
    if (url.indexOf("youtu.be/") > -1) {
      return url.split("youtu.be/")[1].split(/[?#]/)[0];
    }
    if (url.indexOf("/embed/") > -1) {
      return url.split("/embed/")[1].split(/[?#]/)[0];
    }
    return url.split("/").at(-1).split(/[?#]/)[0];
  }

  static formatEmbedUrl(options = {}) {
    let videoURL = options["video-embed-url"] ? options["video-embed-url"] : "";
    if (!videoURL) return "";

    if (videoURL.indexOf("?") == -1) {
      videoURL += "?";
    }

    let videoURLFormatted = videoURL;
    const provider = this.detectProvider(videoURL);
    const enableEndedEvents =
      options.enableEndedEvents === true ||
      options["video-auto-advance"] === "done" ||
      options["auto-continue-trigger"] === "done";

    if (provider === "youtube") {
      const videoId = this.youtubeVideoId(videoURL);
      videoURLFormatted = `https://www.youtube-nocookie.com/embed/${videoId}?`;

      if (options["video-embed-controls"] == "false") {
        videoURLFormatted += "&controls=0";
      } else {
        videoURLFormatted += "&controls=1";
      }

      if (options["video-embed-related"] == "true") {
        videoURLFormatted += "&rel=1";
      } else {
        videoURLFormatted += "&rel=0";
      }

      if (options["video-embed-loop"] == "true") {
        videoURLFormatted += `&playlist=${videoId}`;
      }

      if (enableEndedEvents) {
        videoURLFormatted += "&enablejsapi=1";
        if (
          typeof window !== "undefined" &&
          window.location &&
          window.location.origin
        ) {
          videoURLFormatted += `&origin=${encodeURIComponent(
            window.location.origin
          )}`;
        }
      }
    }

    if (provider === "wistia" && videoURL.indexOf("/medias/") > -1) {
      const videoId = videoURL.split("/medias/")[1].split(/[?#]/)[0];
      videoURLFormatted = `https://fast.wistia.net/embed/iframe/${videoId}?videoFoam=true`;
    }

    if (provider === "vimeo" && videoURL.indexOf("vimeo.com/") > -1) {
      const videoId = videoURL
        .split("vimeo.com/")[1]
        .split(/[?#]/)[0]
        .replace(/^video\//, "");
      videoURLFormatted = `https://player.vimeo.com/video/${videoId}?`;
      if (enableEndedEvents) {
        videoURLFormatted += "&api=1";
      }
    }

    if (videoURLFormatted) {
      if (options["video-embed-autoplay"] == "true") {
        videoURLFormatted += "&autoplay=1";

        if (options["video-embed-start-muted"] == "true") {
          if (provider === "youtube") videoURLFormatted += "&mute=1";
          if (provider === "vimeo") videoURLFormatted += "&background=1";
        }
      } else {
        videoURLFormatted += "&autoplay=0";
      }

      if (options["video-embed-loop"] == "true") {
        videoURLFormatted += "&loop=1";
      } else {
        videoURLFormatted += "&loop=0";
      }
    }

    return videoURLFormatted;
  }

  static iframeAllow(options = {}) {
    let videoOptions =
      "fullscreen; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share;";
    if (options["video-embed-autoplay"] == "true") {
      videoOptions += " autoplay;";
    }
    return videoOptions;
  }

  static ensureIframeSrc(iframe) {
    if (!iframe) return;
    if (!iframe.getAttribute("src")) {
      const dataSrc = iframe.getAttribute("data-src");
      if (dataSrc) iframe.setAttribute("src", dataSrc);
    }
  }

  static bindEnded(iframe, onEnded) {
    if (!iframe || typeof onEnded !== "function") return () => {};

    this.ensureIframeSrc(iframe);

    const src =
      iframe.getAttribute("src") || iframe.getAttribute("data-src") || "";
    const provider = this.detectProvider(src);
    let cleanedUp = false;
    let cleanup = () => {};

    const finish = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      cleanup();
      onEnded();
    };

    if (provider === "youtube") {
      cleanup = this.bindYouTubeEnded(iframe, finish);
    } else if (provider === "vimeo") {
      cleanup = this.bindVimeoEnded(iframe, finish);
    } else if (provider === "wistia") {
      cleanup = this.bindWistiaEnded(iframe, finish);
    }

    return () => {
      if (cleanedUp) return;
      cleanedUp = true;
      cleanup();
    };
  }

  static bindYouTubeEnded(iframe, onEnded) {
    const onMessage = (event) => {
      if (!iframe.contentWindow || event.source !== iframe.contentWindow)
        return;

      let data = event.data;
      try {
        data = typeof data === "string" ? JSON.parse(data) : data;
      } catch (e) {
        return;
      }

      if (!data) return;

      if (data.event === "onStateChange" && data.info === 0) {
        onEnded();
      }
    };

    const notifyListening = () => {
      try {
        if (!iframe.contentWindow) return;
        iframe.contentWindow.postMessage(
          JSON.stringify({
            event: "listening",
            id:
              iframe.id || String(iframe.getAttribute("data-element-id") || ""),
            channel: "widget",
          }),
          "*"
        );
      } catch (e) {}
    };

    window.addEventListener("message", onMessage);
    iframe.addEventListener("load", notifyListening);
    notifyListening();

    // YT sometimes needs a second listening ping after the player boots
    const retry = setTimeout(notifyListening, 1000);

    return () => {
      window.removeEventListener("message", onMessage);
      iframe.removeEventListener("load", notifyListening);
      clearTimeout(retry);
    };
  }

  static bindVimeoEnded(iframe, onEnded) {
    const onMessage = (event) => {
      if (event.origin !== "https://player.vimeo.com") return;
      if (!iframe.contentWindow || event.source !== iframe.contentWindow)
        return;

      let data = event.data;
      try {
        data = typeof data === "string" ? JSON.parse(data) : data;
      } catch (e) {
        return;
      }

      if (!data) return;

      if (data.event === "ready") {
        try {
          iframe.contentWindow.postMessage(
            JSON.stringify({ method: "addEventListener", value: "ended" }),
            event.origin
          );
        } catch (e) {}
      }

      if (data.event === "ended" || data.event === "finish") {
        onEnded();
      }
    };

    window.addEventListener("message", onMessage);

    // Ask for ready if the player is already up
    try {
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ method: "addEventListener", value: "ended" }),
          "*"
        );
      }
    } catch (e) {}

    return () => {
      window.removeEventListener("message", onMessage);
    };
  }

  static bindWistiaEnded(iframe, onEnded) {
    const src =
      iframe.getAttribute("src") || iframe.getAttribute("data-src") || "";
    const match = src.match(
      /^(?:https?:)?\/\/(?:[^/]*\.)?wistia\.(?:net|com)\/.*iframe\/([a-zA-Z0-9]+)/
    );
    if (!match) return () => {};

    const videoId = match[1];
    let unbound = false;

    const queueBind = () => {
      window._wq = window._wq || [];
      window._wq.push({
        id: videoId,
        onReady: (video) => {
          if (unbound) return;
          video.bind("end", () => {
            if (!unbound) onEnded();
          });
        },
      });
    };

    if (
      !document.querySelector(
        'script[src*="fast.wistia.com/assets/external/E-v1.js"]'
      )
    ) {
      const script = document.createElement("script");
      script.src = "https://fast.wistia.com/assets/external/E-v1.js";
      script.async = true;
      script.onload = queueBind;
      document.head.appendChild(script);
    } else {
      queueBind();
    }

    return () => {
      unbound = true;
    };
  }
}

if (typeof window !== "undefined") {
  window.VideoService = VideoService;
}

export default VideoService;
